var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { spatialReferenceSystems, } from '../DataTypes';
import { XMLParser } from 'fast-xml-parser';
import path from 'path';
import { Matrix3, Matrix4, Quaternion, Vector2, Vector3 } from '@skand/math';
import { toECEF, slash } from '../Utils';
const O = new Matrix3().set(1, 0, 0, 0, -1, 0, 0, 0, -1);
export class BentleyLoader {
    constructor(useGda7param = true) {
        this.missingRotations = [];
        this.missingPositions = [];
        this.missingPaths = [];
        this.useGda7param = useGda7param;
    }
    /**
     * Read the coordinate systems of the file
     *
     * @param root Root node of the parsed XML object
     */
    getCoordinateSystems(root) {
        var _a, _b, _c;
        const coordinateSystems = new Map();
        const srsList = (_c = (_b = (_a = root === null || root === void 0 ? void 0 : root[0]) === null || _a === void 0 ? void 0 : _a['SpatialReferenceSystems']) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c['SRS'];
        srsList === null || srsList === void 0 ? void 0 : srsList.forEach((srs) => {
            var _a, _b;
            const id = (_a = srs === null || srs === void 0 ? void 0 : srs['Id']) === null || _a === void 0 ? void 0 : _a[0];
            const definition = (_b = srs === null || srs === void 0 ? void 0 : srs['Definition']) === null || _b === void 0 ? void 0 : _b[0];
            if (id === undefined || definition === undefined) {
                throw new Error('Invalid SRS Id / Definition');
            }
            coordinateSystems.set(id, definition);
        });
        return coordinateSystems;
    }
    /**
     * Get the camera properties of a photo group
     *
     * @param group Photo group
     */
    getCameraProperties(group) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21;
        let sizeX = (_c = (_b = (_a = group === null || group === void 0 ? void 0 : group['ImageDimensions']) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b['Width']) === null || _c === void 0 ? void 0 : _c[0];
        let sizeY = (_f = (_e = (_d = group === null || group === void 0 ? void 0 : group['ImageDimensions']) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e['Height']) === null || _f === void 0 ? void 0 : _f[0];
        let sensorSize = (_g = group === null || group === void 0 ? void 0 : group['SensorSize']) === null || _g === void 0 ? void 0 : _g[0];
        let focalLength = (_h = group === null || group === void 0 ? void 0 : group['FocalLength']) === null || _h === void 0 ? void 0 : _h[0];
        if (sizeX === undefined) {
            throw new Error(`PhotoGroup (name: ${(_k = (_j = group['Name']) === null || _j === void 0 ? void 0 : _j[0]) !== null && _k !== void 0 ? _k : ''}) is missing /ImageDimensions/Width`);
        }
        if (sizeY === undefined) {
            throw new Error(`PhotoGroup (name: ${(_m = (_l = group['Name']) === null || _l === void 0 ? void 0 : _l[0]) !== null && _m !== void 0 ? _m : ''}) is missing /ImageDimensions/Height`);
        }
        if (sensorSize === undefined) {
            throw new Error(`PhotoGroup (name: ${(_p = (_o = group['Name']) === null || _o === void 0 ? void 0 : _o[0]) !== null && _p !== void 0 ? _p : ''}) is missing /SensorSize`);
        }
        if (focalLength === undefined) {
            throw new Error(`PhotoGroup (name: ${(_r = (_q = group['Name']) === null || _q === void 0 ? void 0 : _q[0]) !== null && _r !== void 0 ? _r : ''}) is missing /SensorSize`);
        }
        sizeX = Number(sizeX);
        sizeY = Number(sizeY);
        sensorSize = Number(sensorSize);
        focalLength = Number(focalLength);
        const skew = Number((_t = (_s = group === null || group === void 0 ? void 0 : group['Skew']) === null || _s === void 0 ? void 0 : _s[0]) !== null && _t !== void 0 ? _t : 0);
        const cx = Number((_x = (_w = (_v = (_u = group === null || group === void 0 ? void 0 : group['PrincipalPoint']) === null || _u === void 0 ? void 0 : _u[0]) === null || _v === void 0 ? void 0 : _v['x']) === null || _w === void 0 ? void 0 : _w[0]) !== null && _x !== void 0 ? _x : sizeX / 2);
        const cy = Number((_1 = (_0 = (_z = (_y = group === null || group === void 0 ? void 0 : group['PrincipalPoint']) === null || _y === void 0 ? void 0 : _y[0]) === null || _z === void 0 ? void 0 : _z['y']) === null || _0 === void 0 ? void 0 : _0[0]) !== null && _1 !== void 0 ? _1 : sizeY / 2);
        // Distortion coefficients
        const k1 = Number((_5 = (_4 = (_3 = (_2 = group === null || group === void 0 ? void 0 : group['Distortion']) === null || _2 === void 0 ? void 0 : _2[0]) === null || _3 === void 0 ? void 0 : _3['K1']) === null || _4 === void 0 ? void 0 : _4[0]) !== null && _5 !== void 0 ? _5 : 0);
        const k2 = Number((_9 = (_8 = (_7 = (_6 = group === null || group === void 0 ? void 0 : group['Distortion']) === null || _6 === void 0 ? void 0 : _6[0]) === null || _7 === void 0 ? void 0 : _7['K2']) === null || _8 === void 0 ? void 0 : _8[0]) !== null && _9 !== void 0 ? _9 : 0);
        const k3 = Number((_13 = (_12 = (_11 = (_10 = group === null || group === void 0 ? void 0 : group['Distortion']) === null || _10 === void 0 ? void 0 : _10[0]) === null || _11 === void 0 ? void 0 : _11['K3']) === null || _12 === void 0 ? void 0 : _12[0]) !== null && _13 !== void 0 ? _13 : 0);
        const p1 = Number((_17 = (_16 = (_15 = (_14 = group === null || group === void 0 ? void 0 : group['Distortion']) === null || _14 === void 0 ? void 0 : _14[0]) === null || _15 === void 0 ? void 0 : _15['P1']) === null || _16 === void 0 ? void 0 : _16[0]) !== null && _17 !== void 0 ? _17 : 0);
        const p2 = Number((_21 = (_20 = (_19 = (_18 = group === null || group === void 0 ? void 0 : group['Distortion']) === null || _18 === void 0 ? void 0 : _18[0]) === null || _19 === void 0 ? void 0 : _19['P2']) === null || _20 === void 0 ? void 0 : _20[0]) !== null && _21 !== void 0 ? _21 : 0);
        const imageSize = new Vector2(sizeX, sizeY);
        const principalPoint = new Vector2(cx, cy);
        const distortion = { k1, k2, k3, p1, p2 };
        return {
            skew,
            sensorSize,
            principalPoint,
            imageSize,
            focalLength,
            distortion,
        };
    }
    /**
     * Generate the camera rotation and position transforms
     *
     * @param photo  Photo structure from the parsed XML
     */
    getCameraTransforms(photo) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        // Read rotation fields (column-major)
        const rotationElemNames = [
            'M_00',
            'M_10',
            'M_20',
            'M_01',
            'M_11',
            'M_21',
            'M_02',
            'M_12',
            'M_22',
        ];
        const rotationArray = [];
        for (const n of rotationElemNames) {
            const node = (_e = (_d = (_c = (_b = (_a = photo['Pose']) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b['Rotation']) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d[n]) === null || _e === void 0 ? void 0 : _e[0];
            if (node === undefined) {
                this.missingRotations.push({
                    id: (_f = photo['Id']) === null || _f === void 0 ? void 0 : _f[0],
                    filepath: (_h = (_g = photo['ImagePath']) === null || _g === void 0 ? void 0 : _g[0]) !== null && _h !== void 0 ? _h : '',
                });
                break;
            }
            rotationArray.push(Number(node));
        }
        // Read position fields
        const positionElemNames = ['x', 'y', 'z'];
        const positionArray = [];
        for (const n of positionElemNames) {
            const node = (_o = (_m = (_l = (_k = (_j = photo['Pose']) === null || _j === void 0 ? void 0 : _j[0]) === null || _k === void 0 ? void 0 : _k['Center']) === null || _l === void 0 ? void 0 : _l[0]) === null || _m === void 0 ? void 0 : _m[n]) === null || _o === void 0 ? void 0 : _o[0];
            if (node === undefined) {
                this.missingPositions.push({
                    id: (_p = photo['Id']) === null || _p === void 0 ? void 0 : _p[0],
                    filepath: (_r = (_q = photo['ImagePath']) === null || _q === void 0 ? void 0 : _q[0]) !== null && _r !== void 0 ? _r : '',
                });
                break;
            }
            positionArray.push(Number(node));
        }
        if (rotationArray.length !== rotationElemNames.length ||
            positionArray.length !== positionElemNames.length) {
            return;
        }
        // Change axis order to match renderer
        const rotation = new Matrix3()
            .fromArray(rotationArray)
            .premultiply(O)
            .transpose();
        const position = new Vector3().fromArray(positionArray);
        return { rotation, position };
    }
    /**
     * Convert transforms in projected coordinate system to ECEF.
     *
     * @param P
     * @param R
     * @param proj
     * @returns
     */
    computeECEF(P, R, proj) {
        return __awaiter(this, void 0, void 0, function* () {
            const eps = 0.0001;
            const Px = new Vector3(P.x + eps, P.y, P.z);
            const Py = new Vector3(P.x, P.y + eps, P.z);
            const [position, xOffset, yOffset] = yield toECEF([P, Px, Py], proj, this.useGda7param);
            // Calculate rotation axes
            const xAxis = xOffset.clone().sub(position).normalize();
            const yAxis = yOffset.clone().sub(position).normalize();
            const zAxis = xAxis.clone().cross(yAxis).normalize();
            xAxis.copy(yAxis).cross(zAxis).normalize();
            // Create world rotation matrix
            const basisR = new Matrix4().makeBasis(xAxis, yAxis, zAxis);
            const localR = new Matrix4().setFromMatrix3(R);
            const worldR = basisR.multiply(localR);
            // Convert to quaternion
            const rotation = new Quaternion().setFromRotationMatrix(worldR);
            return { position, rotation };
        });
    }
    /**
     * Process a block from the XML file
     *
     * @param block             Root node of the block
     * @param coordinateSystems Set of all coordinate systems in the XML file
     * @param photoGroup        PhotoGroup object to populate
     */
    processBlock(block, coordinateSystems, photoGroup) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            let cameraId = 0;
            const spatialReferenceProj = coordinateSystems.get((_a = block['SRSId']) === null || _a === void 0 ? void 0 : _a[0]);
            if (spatialReferenceProj === undefined ||
                !(spatialReferenceProj in spatialReferenceSystems)) {
                throw new Error(`Block (name: ${(_c = (_b = block['Name']) === null || _b === void 0 ? void 0 : _b[0]) !== null && _c !== void 0 ? _c : ''}) does not have a valid coordinate system`);
            }
            // Process each photogroup
            const photoGroups = (_f = (_e = (_d = block['Photogroups']) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e['Photogroup']) !== null && _f !== void 0 ? _f : [];
            for (const group of photoGroups) {
                const camera = Object.assign({ id: ++cameraId, spatialReferenceProj }, this.getCameraProperties(group));
                photoGroup.cameras.set(cameraId, camera);
                // Proces each photo
                const photos = (_g = group['Photo']) !== null && _g !== void 0 ? _g : [];
                for (const photo of photos) {
                    const pathString = (_h = photo['ImagePath']) === null || _h === void 0 ? void 0 : _h[0];
                    if (!pathString) {
                        const id = (_j = photo['Id']) === null || _j === void 0 ? void 0 : _j[0];
                        this.missingPaths.push(id);
                        continue;
                    }
                    const slashedPath = slash(pathString);
                    const fileName = path.basename(slashedPath, path.extname(slashedPath));
                    const spatialTransforms = this.getCameraTransforms(photo);
                    if (spatialTransforms === undefined) {
                        continue;
                    }
                    const ecefTransforms = yield this.computeECEF(spatialTransforms.position, spatialTransforms.rotation, spatialReferenceProj);
                    photoGroup.photos.push({
                        cameraId,
                        fileName,
                        position: ecefTransforms.position,
                        rotation: ecefTransforms.rotation,
                    });
                }
            }
        });
    }
    /**
     * Get the photos with missing position data
     */
    getPhotosMissingPositions() {
        return this.missingPositions;
    }
    /**
     * Get the photos with missing position data
     */
    getPhotosMissingRotations() {
        return this.missingRotations;
    }
    /**
     * Get the ids of photos with missing file paths
     */
    getPhotosMissingFilepaths() {
        return this.missingPaths;
    }
    /**
     * Get a `PhotoGroup` object from a Bentley XML input string
     *
     * @param xml XML input string
     */
    read(xml) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // Parse the XML file
            const parser = new XMLParser({
                ignoreAttributes: false,
                allowBooleanAttributes: true,
                isArray: (_, __, ___, isAttribute) => {
                    return !isAttribute;
                },
            });
            const root = parser.parse(xml)['BlocksExchange'];
            const coordinateSystems = this.getCoordinateSystems(root);
            const photoGroup = {
                cameras: new Map(),
                photos: [],
            };
            // Process each block
            const blocks = (_b = (_a = root === null || root === void 0 ? void 0 : root[0]) === null || _a === void 0 ? void 0 : _a['Block']) !== null && _b !== void 0 ? _b : [];
            for (const block of blocks) {
                yield this.processBlock(block, coordinateSystems, photoGroup);
            }
            return photoGroup;
        });
    }
}
